import * as THREE from 'three';

export interface VideoPlane {
  mesh: THREE.Mesh;
  updateFrame: (frame: VideoFrame) => void;
  dispose: () => void;
}

export function createVideoPlane(): VideoPlane {
  // 1×1 unit plane — scaled to match video aspect in 1000-unit world
  const geometry = new THREE.PlaneGeometry(1, 1);

  const material = new THREE.MeshBasicMaterial({
    toneMapped: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.position.set(0, 0, 0.5);

  // Bridge canvas — sized to match the video on first frame
  let bridgeCanvas: HTMLCanvasElement | null = null;
  let ctx2d: CanvasRenderingContext2D | null = null;
  let texture: THREE.CanvasTexture | null = null;
  let currentW = 0;
  let currentH = 0;

  function updateFrame(frame: VideoFrame) {
    const w = frame.displayWidth;
    const h = frame.displayHeight;

    // Recreate canvas + texture when dimensions change.
    // This forces Three.js to allocate a new GPU texture at the correct size.
    if (w !== currentW || h !== currentH) {
      texture?.dispose();

      bridgeCanvas = document.createElement('canvas');
      bridgeCanvas.width = w;
      bridgeCanvas.height = h;
      ctx2d = bridgeCanvas.getContext('2d')!;

      texture = new THREE.CanvasTexture(bridgeCanvas);
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = false;
      texture.colorSpace = THREE.SRGBColorSpace;

      material.map = texture;
      material.needsUpdate = true;

      currentW = w;
      currentH = h;
    }

    // Draw VideoFrame — 2D context converts NV12/I420 → RGBA
    ctx2d!.drawImage(frame, 0, 0, w, h);
    texture!.needsUpdate = true;

    // Scale mesh in the 1000-unit world coordinate system.
    // Height always 1000, width = 1000 * videoAspect.
    const videoAspect = w / h;
    mesh.scale.set(1000 * videoAspect, 1000, 1);
    mesh.position.set(0, 0, 0);
  }

  function dispose() {
    texture?.dispose();
    geometry.dispose();
    material.dispose();
    bridgeCanvas = null;
    ctx2d = null;
    texture = null;
  }

  return { mesh, updateFrame, dispose };
}
